import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiUsers,
  FiTrendingUp,
  FiTarget,
  FiStar
} from 'react-icons/fi';
import stakeholderService from '../../services/stakeholderService';
import CommonModal from '../../services/CommonModal';

const Stakeholders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stakeholders, setStakeholders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
      const confirmDelete = () => {
    handleDelete(selectedStakeholder.id);
    setShowDeleteModal(false);
    setSelectedStakeholder(null);
  };
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    project_id: searchParams.get('project_id') || '',
    stakeholder_type: searchParams.get('stakeholder_type') || '',
    influence_level: searchParams.get('influence_level') || '',
    interest_level: searchParams.get('interest_level') || '',
    status: searchParams.get('status') || '',
    is_key_stakeholder: searchParams.get('is_key_stakeholder') || false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchStakeholders();
    fetchStats();
  }, [searchParams]);

  const fetchStakeholders = async () => {
    try {
      setLoading(true);
      const params = {
        page: searchParams.get('page') || 1,
        limit: searchParams.get('limit') || 10,
        search: searchParams.get('search') || '',
        project_id: searchParams.get('project_id') || '',
        stakeholder_type: searchParams.get('stakeholder_type') || '',
        influence_level: searchParams.get('influence_level') || '',
        interest_level: searchParams.get('interest_level') || '',
        status: searchParams.get('status') || '',
        is_key_stakeholder: searchParams.get('is_key_stakeholder') || false
      };

      const data = await stakeholderService.getStakeholders(params);
      setStakeholders(data.data);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to fetch stakeholders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {
        project_id: searchParams.get('project_id') || ''
      };
      const data = await stakeholderService.getStakeholderStats(params);
      setStats(data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchTerm) {
      newParams.set('search', searchTerm);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        newParams.set(key, filters[key]);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set('page', '1');
    setSearchParams(newParams);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      project_id: '',
      stakeholder_type: '',
      influence_level: '',
      interest_level: '',
      status: '',
      is_key_stakeholder: false
    });
    setSearchTerm('');
    setSearchParams({});
  };

  const handleDelete = async (id) => {

    try {
      await stakeholderService.deleteStakeholder(id);
      toast.success('Stakeholder deleted successfully');
      fetchStakeholders();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete stakeholder');
    }
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage);
    setSearchParams(newParams);
  };

  const getInfluenceBadgeColor = (level) => {
    const colors = {
      Low: 'badge-secondary',
      Medium: 'badge-info',
      High: 'badge-warning',
      Critical: 'badge-danger'
    };
    return colors[level] || 'badge-secondary';
  };

  const getInterestBadgeColor = (level) => {
    const colors = {
      Low: 'badge-secondary',
      Medium: 'badge-success',
      High: 'badge-warning',
      Critical: 'badge-danger'
    };
    return colors[level] || 'badge-secondary';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      Active: 'badge-success',
      Inactive: 'badge-secondary',
      Pending: 'badge-warning'
    };
    return colors[status] || 'badge-secondary';
  };

  if (loading && !stakeholders.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading stakeholders...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stakeholders</h1>
          <p className="text-gray-600 mt-1">Manage project stakeholders and their engagement</p>
        </div>
        <Link
          to="/app/stakeholders/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> Add Stakeholder
        </Link>
      </div>
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Stakeholder"
        message="Are you sure you want to delete this stakeholder?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedStakeholder(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Stakeholders</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <FiUsers className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Key Stakeholders</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.keyStakeholders}</p>
              </div>
              <div className="badge-warning p-3 rounded-lg">
                <FiStar className=" text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Influence</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.byInfluence?.High || 0}
                </p>
              </div>
              <div className="badge-warning p-3 rounded-lg">
                <FiTrendingUp className=" text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Interest</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.byInterest?.High || 0}
                </p>
              </div>
              <div className="badge-success p-3 rounded-lg">
                <FiTarget className=" text-xl" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search stakeholders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <FiFilter /> Filters
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stakeholder Type
                </label>
                <select
                  value={filters.stakeholder_type}
                  onChange={(e) => handleFilterChange('stakeholder_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                  <option value="Customer">Customer</option>
                  <option value="Vendor">Vendor</option>
                  <option value="Partner">Partner</option>
                  <option value="Investor">Investor</option>
                  <option value="Regulatory">Regulatory</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Influence Level
                </label>
                <select
                  value={filters.influence_level}
                  onChange={(e) => handleFilterChange('influence_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Levels</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interest Level
                </label>
                <select
                  value={filters.interest_level}
                  onChange={(e) => handleFilterChange('interest_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Levels</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Stakeholder
                </label>
                <select
                  value={filters.is_key_stakeholder}
                  onChange={(e) => handleFilterChange('is_key_stakeholder', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="true">Key Stakeholders Only</option>
                  <option value="false">Non-Key Stakeholders</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stakeholders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stakeholder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Influence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stakeholders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No stakeholders found
                  </td>
                </tr>
              ) : (
                stakeholders.map((stakeholder) => (
                  <tr key={stakeholder.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">
                              {stakeholder.name}
                            </div>
                            {stakeholder.is_key_stakeholder && (
                              <FiStar className="text-yellow-500" title="Key Stakeholder" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{stakeholder.email}</div>
                          {stakeholder.company && (
                            <div className="text-xs text-gray-400">{stakeholder.company}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {stakeholder.stakeholder_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stakeholder.role || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInfluenceBadgeColor(stakeholder.influence_level)}`}>
                        {stakeholder.influence_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInterestBadgeColor(stakeholder.interest_level)}`}>
                        {stakeholder.interest_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(stakeholder.status)}`}>
                        {stakeholder.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/app/stakeholders/${stakeholder.id}`}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <FiEye />
                        </Link>
                        <Link
                          to={`/app/stakeholders/${stakeholder.id}/edit`}
                          className="text-green-600 hover:text-green-900"
                          title="Edit"
                        >
                          <FiEdit2 />
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedStakeholder(stakeholder);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stakeholders;

